// src/services/authIdentity.ts
import { useAuthStore } from '@/stores/authStore';
import { initRevenueCat, loginRevenueCat } from '@/lib/revenueCat';

interface JsUserShape {
  uid: string;
  isAnonymous: boolean;
  displayName: string | null;
  email: string | null;
  providerData: { providerId: string }[];
}

export type JsAuthIdentity =
  | {
      authenticated: true;
      uid: string;
      displayName: string | null;
      email: string | null;
      provider: 'google' | 'apple';
    }
  | { authenticated: false };

/** Pure: turn the JS-SDK user (the session that persists across cold start) into
 * the UI identity. Prefer the remembered sign-in provider (persisted at sign-in
 * time) — on accounts with BOTH providers linked, the restored user's
 * providerData[0] reflects link order, not the session's actual provider, so it
 * would mislabel an Apple sign-in as Google. Fall back to the providerData[0]
 * heuristic when there's no remembered value (e.g. legacy sessions). */
export function deriveJsAuthIdentity(
  user: JsUserShape | null,
  rememberedProvider: 'google' | 'apple' | null = null,
): JsAuthIdentity {
  if (!user || user.isAnonymous) return { authenticated: false };
  const providerIdStr = user.providerData[0]?.providerId ?? '';
  const provider = rememberedProvider ?? (providerIdStr === 'apple.com' ? 'apple' : 'google');
  return {
    authenticated: true,
    uid: user.uid,
    displayName: user.displayName ?? null,
    email: user.email ?? null,
    provider,
  };
}

/** Side-effecting: drive the auth store + RevenueCat session from the identity. */
export function applyJsAuthIdentity(identity: JsAuthIdentity): void {
  if (identity.authenticated) {
    useAuthStore.getState().setAuthenticated({
      uid: identity.uid,
      displayName: identity.displayName,
      email: identity.email,
      provider: identity.provider,
    });
    initRevenueCat();
    void loginRevenueCat(identity.uid);
  } else {
    useAuthStore.getState().setAnonymous();
  }
}
